import React, { useEffect } from 'react';
import { useQueryClient, QueryClient } from '@tanstack/react-query';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import EpdRoutes from 'routes/EpdRoutes';
import { useFeatureFlags } from 'services/api/queries';
import { FeatureFlagKeyDynamicFormsPortal } from 'services/api/featureFlagsApi';

type Props = RouteComponentProps & {
  component: React.ComponentType<RouteComponentProps>;
  /** Starts the component's own requests while the feature flags load, instead of after them. */
  prefetch?: (queryClient: QueryClient, routeProps: RouteComponentProps) => void;
};

/**
 * Renders the DynamicForms route component only when the DynamicForms.Portal feature flag is enabled.
 * Otherwise redirects to dashboard.
 */
const DynamicFormsGate: React.FC<Props> = ({ component: Component, prefetch, ...rest }) => {
  const { data: flags, isLoading } = useFeatureFlags();
  const queryClient = useQueryClient();

  // Keyed by the URL, so a visit prefetches once however often the gate re-renders.
  useEffect(() => {
    prefetch?.(queryClient, rest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest.match.url]);

  const portalEnabled = flags?.[FeatureFlagKeyDynamicFormsPortal] === true;

  if (isLoading) {
    return null; // or a small spinner
  }
  if (!portalEnabled) {
    return <Redirect to={EpdRoutes.dashboard} />;
  }
  return <Component {...rest} />;
};

export default DynamicFormsGate;
